import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { SubmittalsTab } from '../components/SubmittalsTabEnhanced';
import { RFIsTabEnhanced as RFIsTab } from '../components/RFIsTabEnhanced';
import { BudgetTabEnhanced as BudgetTab } from '../components/BudgetTabEnhanced';
import { MeetingMinutesTab } from '../components/MeetingMinutesTab';
import { ProjectDashboard } from '../components/ProjectDashboard';
import { ChangeOrdersTabEnhanced as ChangeOrdersTab } from '../components/ChangeOrdersTabEnhanced';
import { ProjectResourcesTab } from '../components/ProjectResourcesTab';
import { ScheduleTab } from '../components/ScheduleTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProjectDetail = () => {
  const { projectId } = { projectId: window.location.pathname.split('/').pop() };
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchProject();
    fetchStats();
    fetchUserPermissions();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      toast.error('Failed to load project');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const fetchUserPermissions = async () => {
    try {
      if (user?.role === 'admin') {
        setUserPermissions(['dashboard', 'submittals', 'rfis', 'budget', 'change_orders', 'meeting_minutes', 'resources', 'schedule']);
        return;
      }

      const response = await axios.get(`${API}/projects/${projectId}/members`);
      const currentUserMember = response.data.find(m => m.user_id === user?.id);
      
      if (currentUserMember && currentUserMember.permissions) {
        setUserPermissions(['dashboard', ...currentUserMember.permissions]);
      } else {
        setUserPermissions(['dashboard']);
      }
    } catch (error) {
      console.error('Failed to fetch permissions');
      setUserPermissions(['dashboard']);
    }
  };

  const hasPermission = (permission) => {
    if (user?.role === 'admin') return true;
    const permissionKey = permission.replace('-', '_');
    return userPermissions.includes(permissionKey) || permission === 'dashboard';
  };

  const handleRefreshDashboard = () => {
    fetchStats();
    fetchProject();
    toast.success('Dashboard refreshed');
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingLogo(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const logoUrl = reader.result;
        await axios.patch(`${API}/projects/${projectId}`, { logo_url: logoUrl });
        toast.success('Logo uploaded!');
        fetchProject();
        setUploadingLogo(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload logo');
      setUploadingLogo(false);
    }
  };

  const availableTabs = ['dashboard', 'schedule', 'resources', 'submittals', 'rfis', 'budget', 'change-orders', 'meeting-minutes'].filter(tab => 
    hasPermission(tab)
  );

  if (!project) return <div data-testid="loading-project">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="project-detail-container">
      <div className="bg-white border-b px-8 py-6">
        <div className="flex items-start gap-6">
          <div className="flex-shrink-0">
            {project.logo_url ? (
              <div className="relative group">
                <img 
                  src={project.logo_url} 
                  alt="Project Logo" 
                  className="w-20 h-20 object-contain rounded-lg border border-slate-200 bg-white"
                  data-testid="project-header-logo"
                />
                {user?.role === 'admin' && (
                  <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center cursor-pointer">
                    <span className="text-white text-xs">Change</span>
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  </label>
                )}
              </div>
            ) : user?.role === 'admin' ? (
              <label className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 transition-colors" data-testid="upload-logo-button">
                <div className="text-center">
                  <svg className="w-6 h-6 mx-auto text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-xs text-slate-500 mt-1">Logo</span>
                </div>
                <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploadingLogo} />
              </label>
            ) : null}
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {project.project_number && (
                <span className="text-sm font-mono bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">{project.project_number}</span>
              )}
              {project.internal_reference && (
                <span className="text-sm font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded">Ref: {project.internal_reference}</span>
              )}
            </div>
            <h1 className="text-4xl font-black tracking-tighter text-slate-900">{project.name}</h1>
            {project.client_name && <p className="text-lg text-indigo-600 font-medium mt-1">{project.client_name}</p>}
            {project.location && (
              <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {project.location}
              </p>
            )}
            {project.description && <p className="mt-2 text-slate-600">{project.description}</p>}
          </div>
        </div>
      </div>

      <div className="flex w-full border-b bg-transparent overflow-x-auto">
        {availableTabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`relative h-12 rounded-none border-b-2 px-4 pb-3 pt-3 font-semibold transition-none whitespace-nowrap ${
              activeTab === tab
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
            data-testid={`tab-${tab}`}
          >
            {tab.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
          </button>
        ))}
      </div>

      <div className="p-6">
        {activeTab === 'dashboard' && <ProjectDashboard projectId={projectId} stats={stats} onRefresh={handleRefreshDashboard} project={project} permissions={userPermissions} />}
        {activeTab === 'schedule' && hasPermission('schedule') && <ScheduleTab projectId={projectId} />}
        {activeTab === 'resources' && hasPermission('resources') && <ProjectResourcesTab projectId={projectId} />}
        {activeTab === 'submittals' && hasPermission('submittals') && <SubmittalsTab projectId={projectId} />}
        {activeTab === 'rfis' && hasPermission('rfis') && <RFIsTab projectId={projectId} />}
        {activeTab === 'budget' && hasPermission('budget') && <BudgetTab projectId={projectId} />}
        {activeTab === 'change-orders' && hasPermission('change-orders') && <ChangeOrdersTab projectId={projectId} />}
        {activeTab === 'meeting-minutes' && hasPermission('meeting-minutes') && <MeetingMinutesTab projectId={projectId} />}
        {!hasPermission(activeTab) && activeTab !== 'dashboard' && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔒</div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Access Restricted</h3>
            <p className="text-slate-600">You don't have permission to view this section.</p>
            <p className="text-sm text-slate-500 mt-2">Contact your project administrator to request access.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetail;
